// ════════════════════════════════════════════════════════════════════
// pierPath.js — a way from here to there, for walkers without a keyboard
// ════════════════════════════════════════════════════════════════════
// Built 2026-09-26 for THE AGENT SEAT (Lyraei walking the Pier on her
// own), and usable by anything else that has to walk somewhere later —
// a crowd NPC, click-to-move. Pure: no React, no Firebase. It runs under
// node, and verify/agentpath.mjs proves every door and landmark can be
// reached from SPAWN.
//
// HOW: A* over the SAME `standable()` test the movement loop runs,
// sampled every PATH_STEP tiles, 8-way with no corner cutting. Cells hard
// against a rail or a solid pay a small toll, so routes run down the
// middle of a lane instead of scraping the wall — the loop's wall-slide
// would otherwise stall a walker on a corner. Then the route is string-
// pulled: a waypoint is dropped whenever a straight line between its
// neighbours stays standable, so she walks in lines, not staircases.
//
// ⚑ The first call builds the grid (~a few ms); every call after reuses it.
// The grid is the PLAN's, so moving a building is still a pierPlan.js
// edit and this file never needs touching.
// ════════════════════════════════════════════════════════════════════

import { COLS, ROWS, standable } from "./pierPlan.js";

export const PATH_STEP = 0.25;          // tiles per sample
const W = Math.ceil(COLS / PATH_STEP) + 1;
const H = Math.ceil(ROWS / PATH_STEP) + 1;
const CAP = 4;                           // clearance is only measured this far
const TOLL = [0, 1.6, 0.7, 0.25, 0];     // extra cost by clearance 0..4 (0 = blocked anyway)

let FREE = null, CLEAR = null;
let COST = null, FROM = null, DONE = null;   // findPath scratch, reused
function build() {
  if (FREE) return;
  FREE = new Uint8Array(W * H);
  for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
    FREE[j * W + i] = standable(i * PATH_STEP, j * PATH_STEP) ? 1 : 0;
  }
  // Two-pass 4-neighbour distance to the nearest blocked cell, capped.
  CLEAR = new Uint8Array(W * H);
  for (let k = 0; k < W * H; k++) CLEAR[k] = FREE[k] ? CAP : 0;
  for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
    const k = j * W + i; if (!CLEAR[k]) continue;
    const l = i > 0 ? CLEAR[k - 1] : 0, u = j > 0 ? CLEAR[k - W] : 0;
    CLEAR[k] = Math.min(CLEAR[k], l + 1, u + 1);
  }
  for (let j = H - 1; j >= 0; j--) for (let i = W - 1; i >= 0; i--) {
    const k = j * W + i; if (!CLEAR[k]) continue;
    const r = i < W - 1 ? CLEAR[k + 1] : 0, d = j < H - 1 ? CLEAR[k + W] : 0;
    CLEAR[k] = Math.min(CLEAR[k], r + 1, d + 1);
  }
}

const cellOf = (x, y) => [Math.round(x / PATH_STEP), Math.round(y / PATH_STEP)];
const inside = (i, j) => i >= 0 && j >= 0 && i < W && j < H;
const free = (i, j) => inside(i, j) && FREE[j * W + i] === 1;

// The nearest standable cell to a point, preferring one with room around
// it. Targets are often NOT standable — W'ily's x,y is inside his own
// solid — so every goal is snapped before the search.
function snap(x, y, maxR = 16) {
  const [ci, cj] = cellOf(x, y);
  let best = null, bestScore = Infinity;
  for (let r = 0; r <= maxR; r++) {
    for (let dj = -r; dj <= r; dj++) for (let di = -r; di <= r; di++) {
      if (Math.max(Math.abs(di), Math.abs(dj)) !== r) continue;   // this ring only
      const i = ci + di, j = cj + dj;
      if (!free(i, j)) continue;
      const score = Math.hypot(di, dj) - (CLEAR[j * W + i] >= 2 ? 1.5 : 0);
      if (score < bestScore) { bestScore = score; best = [i, j]; }
    }
    if (best && r >= 2) break;           // give roomier cells two more rings to win
  }
  return best;
}

// A straight line the walker can take. Checked against the GRID, not
// against standable() — standable() walks every solid on the pier per
// call and made string-pulling cost ~50 ms a path. A sample passes only
// if its cell has clearance ≥ 2 (the cell AND its neighbours are free),
// which is stricter than standable(), so a line approved here is a line
// the loop can walk. verify/agentpath.mjs re-walks every leg with the
// exact test at 0.05 tiles to keep this honest.
function lineFree(a, b) {
  const d = Math.hypot(b.x - a.x, b.y - a.y), n = Math.ceil(d / 0.05);
  for (let s = 1; s < n; s++) {
    const t = s / n;
    const [i, j] = cellOf(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
    if (!inside(i, j) || CLEAR[j * W + i] < 2) return false;
  }
  return true;
}

// ── a tiny binary heap on f-score ────────────────────────────────────
function heap() {
  const a = [];
  return {
    get size() { return a.length; },
    add(k, f) {
      a.push([f, k]); let i = a.length - 1;
      while (i) { const p = (i - 1) >> 1; if (a[p][0] <= a[i][0]) break; [a[p], a[i]] = [a[i], a[p]]; i = p; }
    },
    pop() {
      const top = a[0], last = a.pop();
      if (a.length) {
        a[0] = last; let i = 0;
        for (;;) {
          const l = 2 * i + 1, r = l + 1; let m = i;
          if (l < a.length && a[l][0] < a[m][0]) m = l;
          if (r < a.length && a[r][0] < a[m][0]) m = r;
          if (m === i) break; [a[m], a[i]] = [a[i], a[m]]; i = m;
        }
      }
      return top[1];
    },
  };
}

const NB = [[1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1],
            [1, 1, Math.SQRT2], [1, -1, Math.SQRT2], [-1, 1, Math.SQRT2], [-1, -1, Math.SQRT2]];

// findPath({x,y}, {x,y}) → { ok, points:[{x,y}…] (excluding the start), length }
//                       or { ok:false, reason }
export function findPath(from, to) {
  build();
  const s = snap(from.x, from.y, 8), g = snap(to.x, to.y);
  if (!s) return { ok: false, reason: "not standing anywhere walkable" };
  if (!g) return { ok: false, reason: "nowhere to stand near there" };
  const S = s[1] * W + s[0], G = g[1] * W + g[0];
  const gx = g[0], gy = g[1];
  const hOf = (k) => {
    const dx = Math.abs((k % W) - gx), dy = Math.abs(((k / W) | 0) - gy);
    return (dx + dy) + (Math.SQRT2 - 2) * Math.min(dx, dy);           // octile, admissible
  };
  // Scratch buffers are made once and wiped per call — a fresh ~1.6 MB
  // per path was garbage-collector churn every time a follower re-planned.
  if (!COST) { COST = new Float32Array(W * H); FROM = new Int32Array(W * H); DONE = new Uint8Array(W * H); }
  const cost = COST.fill(Infinity), from_ = FROM.fill(-1), done = DONE.fill(0);
  const open = heap();
  cost[S] = 0; open.add(S, hOf(S));
  while (open.size) {
    const k = open.pop();
    if (done[k]) continue;
    done[k] = 1;
    if (k === G) break;
    const i = k % W, j = (k / W) | 0;
    for (const [di, dj, c] of NB) {
      const ni = i + di, nj = j + dj;
      if (!free(ni, nj)) continue;
      if (di && dj && (!free(i + di, j) || !free(i, j + dj))) continue;   // no corner cutting
      const nk = nj * W + ni;
      const nc = cost[k] + c + TOLL[CLEAR[nk]];
      if (nc < cost[nk]) { cost[nk] = nc; from_[nk] = k; open.add(nk, nc + hOf(nk)); }
    }
  }
  if (!done[G]) return { ok: false, reason: "no way through from here" };

  // cells → points, start → goal
  const cells = [];
  for (let k = G; k !== -1; k = from_[k]) cells.push(k);
  cells.reverse();
  const pts = cells.map((k) => ({ x: (k % W) * PATH_STEP, y: ((k / W) | 0) * PATH_STEP }));

  // string-pull from where the walker actually stands
  const out = [];
  let anchor = { x: from.x, y: from.y }, idx = 0;
  while (idx < pts.length - 1) {
    let far = idx + 1;
    for (let t = pts.length - 1; t > idx + 1; t--) {
      if (lineFree(anchor, pts[t])) { far = t; break; }
    }
    out.push(pts[far]); anchor = pts[far]; idx = far;
  }
  if (!out.length) out.push(pts[pts.length - 1]);
  let length = 0, prev = from;
  for (const p of out) { length += Math.hypot(p.x - prev.x, p.y - prev.y); prev = p; }
  return { ok: true, points: out, length };
}
